import { useCallback, useEffect, useMemo, useState, type CSSProperties, type MouseEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isApiConfigured } from "../auth-config";
import type { StudentCohortAssignmentReleased } from "../auth/api-client";
import { getStudentReleasedAssignments } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { useStudentCohorts } from "../student/StudentCohortContext";
import { StudentCohortDetailSkeleton } from "../student/StudentCohortDetailSkeleton";
import { StudentCohortLessonRow } from "../student/StudentCohortLessonRow";
import {
  computeCohortProgressStats,
  findLatestIncomplete,
  findOtherCohortWithIncomplete,
  type OtherCohortIncomplete,
} from "../student/cohortProgress";
import { formatDueCountdown, formatDueDateShort } from "../student/formatDueCountdown";
import { cohortLessonStudentLinkFromAssignment } from "../student/cohortLessonStudentLink";
import { resolveCourseHeroImageUrl } from "../media/courseHeroImage";
import { DEFAULT_COURSE_SCRIM_COLOR, courseScrimColor } from "../teacher/courseScrim";
import { textColorsForBackground } from "../teacher/lessonCardColors";
import { LessonIcon } from "../teacher/LessonIcon";
import { QuizIcon } from "../teacher/QuizIcon";
import { PresentationIcon } from "../teacher/PresentationIcon";
import { fallbackGradient, lessonIconDefinition } from "../teacher/lessonIcons";
import { lessonEpicVisualStyle } from "../teacher/lessonAdvancedVisual";
import { useLessonLaunch } from "../transition/LessonLaunchProvider";

const DEFAULT_DETAILS_BG = "linear-gradient(160deg, #1b4d2e 0%, #0f2a1a 55%, #0a120d 100%)";

function useNowTick(intervalMs: number, enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, enabled]);
  return now;
}

const IMMERSIVE_LIGHT_FG = {
  title: "#fdf9f3",
  blurb: "rgba(253, 249, 243, 0.88)",
  tone: "dark" as const,
};

export default function StudentCohortDetailPage() {
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const launch = useLessonLaunch();
  const { profile, getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const { cohorts, hasCohorts, loading: cohortsLoading, error: cohortError } = useStudentCohorts();
  const [assignments, setAssignments] = useState<StudentCohortAssignmentReleased[]>([]);
  const [loadAssign, setLoadAssign] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherIncomplete, setOtherIncomplete] = useState<OtherCohortIncomplete | null>(null);

  const cohort = useMemo(() => cohorts.find((c) => c.cohortId === cohortId), [cohorts, cohortId]);

  useEffect(() => {
    if (profile?.role === "student" && profile.enrollmentSummary === "archived_only") {
      navigate("/no-active-classes", { replace: true });
    }
  }, [profile?.role, profile?.enrollmentSummary, navigate]);

  const refreshAssignments = useCallback(async () => {
    if (!bearer || !cohortId || !isApiConfigured()) {
      setAssignments([]);
      return;
    }
    setLoadAssign(true);
    setError(null);
    try {
      const data = await getStudentReleasedAssignments(bearer, cohortId);
      setAssignments(data.assignments);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load assignments");
      setAssignments([]);
    } finally {
      setLoadAssign(false);
    }
  }, [bearer, cohortId]);

  useEffect(() => {
    void refreshAssignments();
  }, [refreshAssignments]);

  const progressStats = useMemo(() => computeCohortProgressStats(assignments), [assignments]);
  const quizCount = useMemo(
    () => assignments.filter((a) => a.itemKind === "quiz").length,
    [assignments]
  );
  const presentationCount = useMemo(
    () => assignments.filter((a) => a.itemKind === "presentation").length,
    [assignments]
  );
  const latestIncomplete = useMemo(() => findLatestIncomplete(assignments), [assignments]);
  const caughtUp = hasCohorts && progressStats.total > 0 && progressStats.complete === progressStats.total;
  const hasOpenWithDue = assignments.some((a) => a.open && a.dueAt);
  const nowMs = useNowTick(hasOpenWithDue ? 30_000 : 0, hasOpenWithDue);

  useEffect(() => {
    if (!bearer || !cohortId || !caughtUp || cohorts.length < 2) {
      setOtherIncomplete(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const found = await findOtherCohortWithIncomplete(bearer, cohorts, cohortId);
      if (!cancelled) setOtherIncomplete(found);
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, cohortId, caughtUp, cohorts]);

  const accent = cohort?.accentColor?.trim() || null;
  const heroImageUrl = cohort ? resolveCourseHeroImageUrl(cohort) : null;
  const hasHeroImage = Boolean(heroImageUrl);
  const pageBackground = accent || DEFAULT_DETAILS_BG;
  const scrimColor = courseScrimColor(accent);
  const pageColors = useMemo(() => {
    if (hasHeroImage || accent) return IMMERSIVE_LIGHT_FG;
    return textColorsForBackground(pageBackground);
  }, [hasHeroImage, accent, pageBackground]);

  const upNextIsQuiz = latestIncomplete?.itemKind === "quiz" && latestIncomplete.quizId;
  const upNextIsPresentation =
    latestIncomplete?.itemKind === "presentation" && latestIncomplete.presentationId;
  const upNextItemKey = latestIncomplete
    ? upNextIsQuiz
      ? latestIncomplete.quizId!
      : upNextIsPresentation
        ? latestIncomplete.presentationId!
        : latestIncomplete.lessonId ?? latestIncomplete.assignmentId
    : "";
  const upNextUrl = latestIncomplete
    ? cohortLessonStudentLinkFromAssignment(cohortId ?? "", latestIncomplete)
    : null;

  const onUpNextOpen = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!latestIncomplete || !upNextUrl || upNextIsQuiz || upNextIsPresentation) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    const epic = lessonEpicVisualStyle({
      workspaceMode: latestIncomplete.workspaceMode,
      baseColor: latestIncomplete.iconBgColor,
      fallbackSeed: upNextItemKey,
    });
    const background =
      epic?.innerBackground ?? latestIncomplete.iconBgColor?.trim() ?? fallbackGradient(upNextItemKey);
    const watermarkColor =
      latestIncomplete.iconFgColor?.trim() || textColorsForBackground(background).title;
    launch({
      to: upNextUrl,
      background,
      watermarkColor,
      iconDef: lessonIconDefinition(latestIncomplete.iconName),
    });
  };

  if (!cohortId) {
    return (
      <div className="student-page-inner">
        <p className="warn-banner">Missing cohort id.</p>
      </div>
    );
  }

  if ((cohortsLoading && !cohort) || (loadAssign && !cohort && !cohorts.length)) {
    return <StudentCohortDetailSkeleton />;
  }

  if (!hasCohorts) {
    return <NavigateToJoin />;
  }

  if (!cohort && !cohortsLoading) {
    const first = cohorts[0]?.cohortId;
    if (first) {
      return (
        <div className="student-page-inner">
          <div className="warn-banner" role="alert">
            That cohort was not found.{" "}
            <Link to={`/cohorts/${first}`}>Go to your cohort</Link>
          </div>
        </div>
      );
    }
    return <NavigateToJoin />;
  }

  if (!cohort) {
    return <StudentCohortDetailSkeleton />;
  }

  return (
    <div
      className="student-cohort-details"
      data-tone={pageColors.tone}
      style={
        {
          "--course-details-bg": pageBackground,
          "--course-details-scrim": scrimColor || DEFAULT_COURSE_SCRIM_COLOR,
          "--course-details-fg": pageColors.title,
          "--course-details-muted": pageColors.blurb,
        } as CSSProperties
      }
    >
      <div
        className={`student-cohort-details-hero${hasHeroImage ? " student-cohort-details-hero--image" : ""}`}
      >
        {heroImageUrl ? (
          <img
            className="student-cohort-details-hero-img"
            src={heroImageUrl}
            alt=""
            crossOrigin="anonymous"
          />
        ) : null}
        <div className="student-cohort-details-hero-scrim" aria-hidden />
        <div className="student-cohort-details-hero-inner">
          <p className="student-cohort-details-eyebrow">{cohort.label}</p>
          <h1 className="student-cohort-details-title">{cohort.courseTitle}</h1>
          <p className="student-cohort-details-lede">
            {cohort.courseDescription?.trim() || "Your assigned lessons, quizzes, and progress for this class."}
          </p>
          <dl className="student-cohort-details-stats">
            <div>
              <dt>Items</dt>
              <dd>{progressStats.total}</dd>
            </div>
            {quizCount > 0 ? (
              <div>
                <dt>Quizzes</dt>
                <dd>{quizCount}</dd>
              </div>
            ) : null}
            {presentationCount > 0 ? (
              <div>
                <dt>Presentations</dt>
                <dd>{presentationCount}</dd>
              </div>
            ) : null}
            <div>
              <dt>Complete</dt>
              <dd>{progressStats.complete}</dd>
            </div>
            <div>
              <dt>Open now</dt>
              <dd>{progressStats.open}</dd>
            </div>
          </dl>
        </div>
      </div>

      {error || cohortError ? (
        <div className="warn-banner student-cohort-details-pad" role="alert">
          {error ?? cohortError}
        </div>
      ) : null}

      <section className="student-cohort-details-section student-cohort-details-pad">
        <div className="student-cohort-details-up-next">
        {loadAssign && assignments.length === 0 ? (
          <p className="student-cohort-details-muted">Loading your lessons…</p>
        ) : caughtUp ? (
          <>
            <h2 className="student-cohort-details-section-title">You&apos;re caught up!</h2>
            <p className="student-cohort-details-muted">
              You&apos;ve completed all {progressStats.total} assigned item
              {progressStats.total === 1 ? "" : "s"} in this cohort. Nice work.
            </p>
            {otherIncomplete ? (
              <p className="student-cohort-details-muted" style={{ marginTop: "0.65rem" }}>
                You still have open work in{" "}
                <Link
                  to={`/cohorts/${otherIncomplete.cohort.cohortId}`}
                  className="student-cohort-details-link"
                >
                  {otherIncomplete.cohort.courseTitle} · {otherIncomplete.cohort.label}
                </Link>
                : {otherIncomplete.assignment.lessonTitle}.
              </p>
            ) : null}
          </>
        ) : latestIncomplete ? (
          <>
            <h2 className="student-cohort-details-section-title">Up next</h2>
            <div className="student-cohort-details-up-next-row">
              {upNextIsQuiz ? (
                <QuizIcon
                  quizId={latestIncomplete.quizId!}
                  title={latestIncomplete.lessonTitle}
                  iconName={latestIncomplete.iconName}
                  bgColor={latestIncomplete.iconBgColor}
                  fgColor={latestIncomplete.iconFgColor}
                  size={3.25}
                />
              ) : upNextIsPresentation ? (
                <PresentationIcon
                  title={latestIncomplete.lessonTitle}
                  iconName={latestIncomplete.iconName}
                  bgColor={latestIncomplete.iconBgColor}
                  fgColor={latestIncomplete.iconFgColor}
                  size={3.25}
                />
              ) : (
                <LessonIcon
                  lessonId={latestIncomplete.lessonId ?? upNextItemKey}
                  title={latestIncomplete.lessonTitle}
                  iconName={latestIncomplete.iconName}
                  bgColor={latestIncomplete.iconBgColor}
                  fgColor={latestIncomplete.iconFgColor}
                  workspaceMode={latestIncomplete.workspaceMode}
                  size={3.25}
                />
              )}
              <div className="student-cohort-details-up-next-copy">
                <strong>{latestIncomplete.lessonTitle}</strong>
                <p className="student-cohort-details-muted small" style={{ margin: "0.25rem 0 0" }}>
                  {latestIncomplete.dueAt ? (
                    <>
                      Due {formatDueDateShort(latestIncomplete.dueAt)} ·{" "}
                      {formatDueCountdown(latestIncomplete.dueAt, nowMs)}
                    </>
                  ) : (
                    "No due date"
                  )}
                </p>
                {upNextUrl ? (
                  <Link
                    to={upNextUrl}
                    onClick={onUpNextOpen}
                    className="btn primary"
                    style={{ marginTop: "0.65rem", textDecoration: "none", display: "inline-flex" }}
                  >
                    {upNextIsQuiz
                      ? "Open quiz"
                      : upNextIsPresentation
                        ? "Open slides"
                        : "Open lesson"}
                  </Link>
                ) : null}
              </div>
              <p className="student-cohort-details-up-next-lesson-desc">
                {latestIncomplete.lessonDescription?.trim() ||
                  (upNextIsPresentation
                    ? "Open these slides to preview the ideas you will practice next."
                    : "Open this lesson to see what you will build and practice.")}
              </p>
            </div>
          </>
        ) : progressStats.total === 0 ? (
          <>
            <h2 className="student-cohort-details-section-title">Waiting on assignments</h2>
            <p className="student-cohort-details-muted">
              Your teacher hasn&apos;t assigned anything yet. Check back soon.
            </p>
          </>
        ) : (
          <>
            <h2 className="student-cohort-details-section-title">Keep going</h2>
            <p className="student-cohort-details-muted">
              {progressStats.complete} of {progressStats.total} assigned item
              {progressStats.total === 1 ? "" : "s"} complete.
            </p>
          </>
        )}
        </div>
      </section>

      <section className="student-cohort-details-section student-cohort-details-pad">
        <h2 className="student-cohort-details-section-title">Course plan</h2>
        {loadAssign ? (
          <p className="student-cohort-details-muted">Loading assignments…</p>
        ) : assignments.length === 0 ? (
          <p className="student-cohort-details-muted">Nothing on the cohort plan yet.</p>
        ) : (
          <ol className="student-cohort-details-lessons">
            {assignments.map((a, index) => (
              <StudentCohortLessonRow
                key={a.assignmentId}
                assignment={a}
                cohortId={cohortId}
                index={index}
                nowMs={nowMs}
              />
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

function NavigateToJoin() {
  return (
    <main className="page page-align-start student-page-inner">
      <div className="card card-wide">
        <p className="eyebrow">Student</p>
        <h1>Join a cohort</h1>
        <p className="lede">Ask your teacher for a join code to see your class here.</p>
        <Link to="/join" className="btn primary" style={{ textDecoration: "none" }}>
          Join a cohort
        </Link>
      </div>
    </main>
  );
}
